import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* ======================= Helpers (jak w PercentOfNumberTask) ======================= */

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  if (a === 0 && b === 0) return 1;
  while (b !== 0) {
    const t = b;
    b = a % b;
    a = t;
  }
  return a === 0 ? 1 : a;
};

const simplifyFraction = (num, den) => {
  const g = gcd(num, den);
  return {
    numerator: num / g,
    denominator: den / g,
  };
};

const fractionToLatex = ({ numerator, denominator }) => {
  if (!denominator || denominator === 0) return "";
  if (denominator === 1) return `${numerator}`;
  return `\\dfrac{${numerator}}{${denominator}}`;
};

const improperToMixedLatex = ({ numerator, denominator }) => {
  if (!denominator || denominator === 0) return "";

  const sign = numerator < 0 ? -1 : 1;
  const absNum = Math.abs(numerator);

  if (absNum < denominator) {
    return fractionToLatex({ numerator, denominator });
  }

  const whole = Math.floor(absNum / denominator);
  const rem = absNum % denominator;

  if (rem === 0) {
    return `${sign * whole}`;
  }

  const fracPart = `\\dfrac{${rem}}{${denominator}}`;
  if (sign === -1) return `-${whole}\\;${fracPart}`;
  return `${whole}\\;${fracPart}`;
};

const isValidFractionObject = (f) =>
  f &&
  typeof f === "object" &&
  f.numerator !== undefined &&
  f.denominator !== undefined;

const areFractionsEqual = (a, b) => {
  if (!isValidFractionObject(a) || !isValidFractionObject(b)) return false;

  const an = Number(a.numerator);
  const ad = Number(a.denominator);
  const bn = Number(b.numerator);
  const bd = Number(b.denominator);

  if (!isFinite(an) || !isFinite(ad) || !isFinite(bn) || !isFinite(bd)) {
    return false;
  }
  if (ad === 0 || bd === 0) return false;

  return an * bd === bn * ad;
};

const parseFractionFromString = (input) => {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  let sign = 1;
  let rest = trimmed;
  if (rest[0] === "-" || rest[0] === "+") {
    if (rest[0] === "-") sign = -1;
    rest = rest.slice(1).trim();
  }

  let whole = 0;
  let num;
  let den;

  const spaceParts = rest.split(/\s+/);

  if (spaceParts.length === 2) {
    whole = parseInt(spaceParts[0], 10);
    if (!Number.isFinite(whole)) return null;

    const fracParts = spaceParts[1].split("/");
    if (fracParts.length !== 2) return null;

    num = parseInt(fracParts[0], 10);
    den = parseInt(fracParts[1], 10);
    if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;

    num = sign * (whole * den + num);
  } else {
    const fracParts = rest.split("/");
    if (fracParts.length === 1) {
      num = sign * parseInt(fracParts[0], 10);
      den = 1;
      if (!Number.isFinite(num)) return null;
    } else if (fracParts.length === 2) {
      num = sign * parseInt(fracParts[0], 10);
      den = parseInt(fracParts[1], 10);
      if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
    } else {
      return null;
    }
  }

  return simplifyFraction(num, den);
};

const parseDecimalFromString = (input) => {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(",", ".");
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  return value;
};

/* ======================= Generate ======================= */
/**
 * Zadanie: Dany jest procent pewnej liczby – znajdź tę liczbę.
 *
 * difficulty: "easy"  – procenty ładne, wynik liczbą całkowitą,
 *             "hard"  – procenty „brzydsze”, wynik może być ułamkiem.
 */
export function generateTask(config = {}) {
  const { difficulty = "easy" } = config;

  let percent;
  let givenPart;      // wartość znanego procentu
  let wholeFraction;  // szukana liczba (jako ułamek)

  if (difficulty === "hard") {
    // procenty niebędące wielokrotnościami 5
    const hardPercents = [];
    for (let p = 11; p <= 45; p++) {
      if (p % 5 !== 0) hardPercents.push(p);
    }
    percent = hardPercents[randInt(0, hardPercents.length - 1)];

    // dana wartość procentu – całkowita, trochę większa
    givenPart = randInt(30, 200);

    // x = givenPart * 100 / percent
    const num = givenPart * 100;
    const den = percent;
    wholeFraction = simplifyFraction(num, den);
  } else {
    // easy: ładne procenty
    const easyPercents = [10, 20, 25, 50];
    percent = easyPercents[randInt(0, easyPercents.length - 1)];

    // szukana liczba – wielokrotność 20, żeby wszystkie procenty dawały całkowity wynik
    const whole = 20 * randInt(2, 10); // 40, 60, ..., 200

    // dana w treści wartość procentu
    givenPart = (percent * whole) / 100; // całkowita

    wholeFraction = { numerator: whole, denominator: 1 };
  }

  return {
    type: "PercentFindWholeTask",
    data: {
      difficulty,
      percent,
      givenPart,
      wholeFraction,
    },
  };
}

/* ======================= Render task ======================= */

export function renderTask({ data }) {
  const { percent, givenPart } = data;

  return (
    <p>
      <InlineMath math={`${percent}\\%`} />{" "}
      pewnej liczby jest równe{" "}
      <InlineMath math={`${givenPart}`} />. Oblicz tę liczbę.
    </p>
  );
}

/* ======================= Input (FractionInput) ======================= */

export function renderInput(_, userAnswer, onChange, disabled) {
  const handleChange = (ans) => onChange?.(ans);

  return (
    <div style={{ marginTop: "0.75rem" }}>
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        placeholder="np. 3/4, 1 1/2, 0.75"
        label=""
      />
    </div>
  );
}

/* ======================= Check ======================= */

export function checkAnswer({ data }, userAnswer) {
  const expected = data.wholeFraction;
  const expectedValue = expected.numerator / expected.denominator;

  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return {
      isCorrect: false,
      expected,
      actual: null,
      comment: null,
    };
  }

  const raw = String(userAnswer.raw).trim();

  // 1) ułamek zwykły / mieszany
  const parsedFrac = parseFractionFromString(raw);
  if (parsedFrac && areFractionsEqual(expected, parsedFrac)) {
    return {
      isCorrect: true,
      expected,
      actual: parsedFrac,
      comment: null,
    };
  }

  // 2) zapis dziesiętny
  const decimalVal = parseDecimalFromString(raw);
  if (decimalVal !== null) {
    const approxNum = Math.round(decimalVal * 100);
    const approxDen = 100;
    const approxFrac = simplifyFraction(approxNum, approxDen);

    const sameFraction = areFractionsEqual(expected, approxFrac);
    const closeEnough = Math.abs(decimalVal - expectedValue) <= 0.005;

    const isCorrect = sameFraction || closeEnough;

    return {
      isCorrect,
      expected,
      actual: approxFrac,
      comment: null,
    };
  }

  return {
    isCorrect: false,
    expected,
    actual: null,
    comment: null,
  };
}

/* ======================= Answer & User Answer ======================= */

export function renderAnswer({ data }) {
  const { wholeFraction } = data;
  const latex = improperToMixedLatex(wholeFraction);

  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <InlineMath math={latex} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer !== "object" ||
    !userAnswer.raw ||
    !String(userAnswer.raw).trim()
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const raw = String(userAnswer.raw).trim();

  const parsedFrac = parseFractionFromString(raw);
  if (parsedFrac) {
    const latex = fractionToLatex(parsedFrac);
    return (
      <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
        <InlineMath math={latex} />
      </div>
    );
  }

  const decimalVal = parseDecimalFromString(raw);
  if (decimalVal !== null) {
    const normalized = raw.replace(",", ".");
    return (
      <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
        <InlineMath math={normalized} />
      </div>
    );
  }

  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <InlineMath math={raw} />
    </div>
  );
}

/* ======================= Explanation ======================= */

export function renderExplanation({ data }) {
  const { percent, givenPart, wholeFraction } = data;

  const step1 = `\\dfrac{${percent}}{100}x = ${givenPart}`;
  const step2 = `x = \\dfrac{${givenPart}\\cdot 100}{${percent}}`;
  const mixed = improperToMixedLatex(wholeFraction);
  const step3 = `x = ${mixed}`;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginTop: 12,
      }}
    >
      <p>
        <strong>Krok 1:</strong> Zapisz informację z treści w postaci równania.
      </p>
      <BlockMath math={step1} />

      <p>
        <strong>Krok 2:</strong> Podziel obie strony równania przez{" "}
        <InlineMath math={`\\dfrac{${percent}}{100}`} /> i zapisz{" "}
        <InlineMath math={"x"} />.
      </p>
      <BlockMath math={step2} />

      <p>
        <strong>Krok 3:</strong> Uprość ułamek po prawej stronie.
      </p>
      <BlockMath math={step3} />
    </div>
  );
}
